import { gql, type InMemoryCache } from '@apollo/client';
import type { ReadFieldFunction } from '@apollo/client/cache/core/types/common';
import { cloneDeep } from '@apollo/client/utilities';

import { sendErrorEvent } from '@trello/error-reporting';

import type {
  Action_Type,
  TrelloAction,
  TrelloAppCreator,
  TrelloCardActions,
  TrelloMember,
} from '../generated';
import {
  isEnumString,
  isObject,
  isString,
} from '../plannerCardDataMapping/validateHelpers';
import type {
  IncomingNativeModel,
  RecursivePartial,
  TargetModel,
} from './cacheModelTypes';
import { getObjectIdFromCacheObject } from './getObjectIdFromCacheObject';
import { syncNativeNestedObjectToRest } from './syncNativeNestedObjectToRest';
import { syncNativeToRestScalars } from './syncNativeToRestScalars';
import { syncNestedModelReference } from './syncNestedModelReference';

const validActionTypes: Action_Type[] = [
  'acceptEnterpriseJoinRequest',
  'addAdminToEnterprise',
  'addAttachmentToCard',
  'addChecklistToCard',
  'addMemberToBoard',
  'addMemberToCard',
  'addMemberToOrganization',
  'addOrganizationToEnterprise',
  'addToEnterprisePluginWhitelist',
  'addToOrganizationBoard',
  'butlerRuleFailedBoard',
  'changeEnterprisePublicBoardVisibility',
  'commentCard',
  'convertToCardFromCheckItem',
  'copyBoard',
  'copyCard',
  'copyInboxCard',
  'copyCommentCard',
  'createBoard',
  'createCard',
  'createInboxCard',
  'createCustomField',
  'createEnterpriseJoinRequest',
  'createList',
  'createOrganization',
  'deactivatedMemberInEnterprise',
  'deactivatedMemberInOrganization',
  'declineEnterpriseJoinRequest',
  'deleteAttachmentFromCard',
  'deleteBoardInvitation',
  'deleteCard',
  'deleteCustomField',
  'deleteList',
  'deleteOrganizationInvitation',
  'disableEnterprisePluginWhitelist',
  'disableIssuingOfConsentTokensInEnterprise',
  'disablePlugin',
  'disablePowerUp',
  'emailCard',
  'enableEnterprisePluginWhitelist',
  'enableIssuingOfConsentTokensInEnterprise',
  'enablePlugin',
  'enablePowerUp',
  'enterpriseAdminViewedPrivateBoard',
  'makeAdminOfBoard',
  'makeNormalMemberOfBoard',
  'makeNormalMemberOfOrganization',
  'makeObserverOfBoard',
  'memberJoinedTrello',
  'moveCardFromBoard',
  'moveCardToBoard',
  'moveInboxCardToBoard',
  'moveListFromBoard',
  'moveListToBoard',
  'reactivatedMemberInEnterprise',
  'reactivatedMemberInOrganization',
  'removeAdminFromEnterprise',
  'removeChecklistFromCard',
  'removeDeprecatedPlugin',
  'removedAllTokensFromOwnedMembers',
  'removedTokensFromMember',
  'removeFromEnterprisePluginWhitelist',
  'removeFromOrganizationBoard',
  'removeMemberFromCard',
  'removeOrganizationFromEnterprise',
  'trelloAddAdminToEnterprise',
  'trelloAddOrganizationToEnterprise',
  'trelloRemoveAdminFromEnterprise',
  'trelloRemoveOrganizationFromEnterprise',
  'unconfirmedBoardInvitation',
  'unconfirmedOrganizationInvitation',
  'updateBoard',
  'updateBoardOrg',
  'updateBoardVisibility',
  'updateCard',
  'updateCheckItemStateOnCard',
  'updateChecklist',
  'updateEnterprise',
  'updateCustomField',
  'updateCustomFieldItem',
  'updateList',
  'updateMember',
  'updateOrganization',
  'withdrawEnterpriseJoinRequest',
  'grantEnterpriseLicense',
  'revokeEnterpriseLicense',
  'grantOrganizationLicenses',
  'updateCardIdList',
  'updateCardClosed',
  'updateCardDue',
  'updateCardDueComplete',
  'updateListClosed',
];

export const fieldMappings = {
  type: {
    validate: (value: unknown) => isEnumString(value, validActionTypes),
    sendValueToSentry: true,
  },
  date: { validate: isString },
};

const dataFields = [
  'board',
  'card',
  'attachment',
  'checklist',
  'checkItem',
  'member',
  'cardSource',
  'list',
  'sourceCard',
  'creationMethod',
  'listBefore',
  'listAfter',
  'customField',
  'customFieldItem',
  'dateLastEdited',
];

/**
 * Helper function to sync the non-native Action.data field, which
 * is stored as a JSON string containing miscellaneous fields on
 * the action.
 *
 * On the incoming TrelloCardAction model, fields such as
 * board, card, etc are nested under the action.
 * @param action The action to write to the cache
 * @param incoming The incoming native TrelloAction data
 * @param cache The cache to write to
 * @param readField The readField function
 */

const syncActionData = (
  action: TargetModel,
  incoming: RecursivePartial<TrelloCardActions>,
  cache: InMemoryCache,
  readField: ReadFieldFunction,
) => {
  const data: Record<string, unknown> = {};

  Object.entries(incoming).forEach(
    ([key, value]: [string, IncomingNativeModel]) => {
      if (!dataFields.includes(key)) {
        return;
      }

      if (key === 'dateLastEdited' && value !== null) {
        data[key] = value;
        return;
      }

      // For all other data fields, just sync the id
      try {
        const id = getObjectIdFromCacheObject(value, readField);
        if (id) {
          data[key] = { id };
        }
      } catch (err) {
        sendErrorEvent(err);
      }
    },
  );

  const stringifiedData = JSON.stringify(data);

  /**
   * The data field doesn't exist in the native TrelloAction model, so we can't
   * use one of our helpers to sync this field.
   */
  cache.writeFragment({
    id: cache.identify({ __typename: 'Action', id: action.id }),
    fragment: gql`
      fragment ActionData on Action {
        data
      }
    `,
    data: {
      __typename: 'Action',
      id: action.id,
      data: stringifiedData,
    },
  });
};

const displayfieldMappings = {
  displayKey: { validate: isString, key: 'translationKey' },
  displayEntities: {
    validate: (val: unknown) => {
      // Check that it's an object
      if (isObject(val)) {
        // Check that each entry in the object is [string, Record<string, unknown> | null]
        const isValidEntity = (ent: unknown) => ent === null || isObject(ent);
        return Object.entries(val).every(
          ([key, entity]) =>
            typeof key === 'string' &&
            (key === '__typename'
              ? typeof entity === 'string'
              : isValidEntity(entity)),
        );
      }
      return false;
    },
    key: 'entities',
    transform: (val: Record<string, Record<string, unknown> | null>) => {
      // Clone
      const displayEntities = cloneDeep(val);

      delete displayEntities.__typename;

      Object.entries(displayEntities).forEach(([entityName, entity]) => {
        // Sometimes an entity will be null (e.g. TrelloAddMemberToCard -> member will be null if you add yourself to a card)
        // We don't want to write these to the cache
        if (entity === null) {
          delete displayEntities[entityName];
          return;
        }

        // Transform non-null entities
        delete entity.__typename;

        if ('objectId' in entity) {
          entity['id'] = entity.objectId;
          delete entity.objectId;
        }

        // Remove null fields from each entity
        // e.g. not all AttachmentPreview display entities will have a previewUrl
        Object.entries(entity).forEach(([key, value]) => {
          if (value === null) {
            delete entity[key];
          }
        });
      });

      // Serialize to JSON string
      return JSON.stringify(displayEntities);
    },
  },
};

const generateActionDisplayFragment = (field: string) => {
  return `fragment Action${field}Write on Action {
    id
    display {
      ${field}
    }
  }`;
};

const generateActionDisplayData = (
  id: string,
  field: string,
  value: unknown,
) => {
  return {
    __typename: 'Action',
    id,
    display: {
      __typename: 'Action_Display',
      [field]: value,
    },
  };
};

/**
 * Given native TrelloAction data, writes all action data to the Action model
 * in the Apollo Cache
 * @param incoming A partial TrelloAction model
 * @param cache The cache to write to
 * @param readField The readField function
 */
export const syncTrelloActionToAction = (
  incoming: RecursivePartial<TrelloCardActions>,
  cache: InMemoryCache,
  readField: ReadFieldFunction,
) => {
  try {
    const actionId = getObjectIdFromCacheObject(incoming, readField);
    const action: TargetModel = { type: 'Action', id: actionId };
    syncNativeToRestScalars<TrelloAction>(
      action,
      fieldMappings,
      incoming,
      cache,
      readField,
    );

    const member = readField<TrelloMember>('creator', incoming);

    if (member) {
      const memberId = getObjectIdFromCacheObject(member, readField);
      syncNestedModelReference(
        action,
        {
          model: { type: 'Member', id: memberId },
          fieldName: 'memberCreator',
          idFieldName: 'idMemberCreator',
        },
        cache,
      );
    }

    const appCreator = readField<TrelloAppCreator>('appCreator', incoming);
    if (appCreator) {
      const appCreatorId = getObjectIdFromCacheObject(appCreator, readField);
      syncNestedModelReference(
        action,
        {
          model: { type: 'AppCreator', id: appCreatorId },
          fieldName: 'appCreator',
          idFieldName: 'idAppCreator',
        },
        cache,
      );
    }

    syncActionData(action, incoming, cache, readField);
    syncNativeNestedObjectToRest(
      action,
      displayfieldMappings,
      generateActionDisplayFragment,
      generateActionDisplayData,
      // @ts-expect-error TS(2345): Types of property '__typename' are incompatible. I promise __typename will be a string
      incoming,
      cache,
    );
  } catch (err) {
    sendErrorEvent(err);
  }
};
